import { Col, Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import _timezone from 'dayjs/plugin/timezone';
import { Store } from 'rc-field-form/lib/interface';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import emiseApi, { ISezemiseKorpAkce } from 'services/korporatniAkce/emise';
import kontrolaXMLApi, { kontrolaXMLApiCert } from 'services/korporatniAkce/kontrolaXMLSRD';
import zalozeniSRDApi from 'services/korporatniAkce/zalozeniSRD';

import { ETypUdalosti } from 'enums/akcionarKorpAkce/typUdalosti';
import routePathEnum from 'enums/routePathEnum';

import { novaOznameni } from 'data/codebooks/korporatniAkce/novaOznameni';
import { zpusobZadani } from 'data/codebooks/korporatniAkce/zpusobZadani';

console.log(_timezone);
let utc = require('dayjs/plugin/utc');
let timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc);
dayjs.extend(timezone);

const tz = 'Europe/Prague';

export const NovaUdalost: React.FC = () => {
    const [form] = Form.useForm();
    const { aktivniFirma, menuProfile, idSRD, profily, NotificationComponent } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const location = useLocation();
    const { setLevel } = useProfily();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingPXklic, setLoadingPXklic] = useState<boolean>(false);
    const [dataIsin, setDataIsin] = useState<ISezemiseKorpAkce[]>([]);
    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadCheckError, setUploadCheckError] = useState<boolean>(false);
    const [currentIsin, setCurrentIsin] = useState<string>('');
    const [fileList, setFileList] = useState<IFileList[]>([]);
    const [data, setData] = useState<{
        oznameni: ETypUdalosti | null;
        isin: string | null;
        zadani: string | null;
        datumzverejneni: string | null;
    }>({
        oznameni: null,
        isin: null,
        zadani: null,
        datumzverejneni: null
    });

    useEffect(() => {
        dayjs.tz.setDefault('Europe/Prague');
        setLoading(true);
        emiseApi()
            .then(resp => setDataIsin(resp.data.sezemise))
            .finally(() => setLoading(false));
        return () => {
            dayjs.tz.setDefault();
        };
    }, []);

    const onFinish = (value: Store) => {
        profily?.typprihlaseni !== 'PXKLIC' ? setLoading(true) : setLoadingPXklic(true);
        idSRD &&
            data.zadani === 'SRD_NOVA_ZPUSOB_XML' &&
            (profily?.typprihlaseni === 'CERT'
                ? kontrolaXMLApiCert(
                      idSRD,
                      fileList[0].fileName,
                      fileList[0].name,
                      data?.oznameni,
                      data.datumzverejneni
                  )
                : kontrolaXMLApi(idSRD, fileList[0].fileName, fileList[0].name, data?.oznameni, data.datumzverejneni)
            )
                ?.then(resp => {
                    if ((resp.data.upozor || []).length) {
                        resp.data.upozor.map(e =>
                            NotificationComponent({
                                title: e.typ
                            })
                        );
                    } else if (data.zadani === 'SRD_NOVA_ZPUSOB_XML') {
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.KORPORATNI_AKCE}`);
                    } else {
                        data.oznameni === ETypUdalosti.VH &&
                            navigateWorkaround(`${location.pathname}${routePathEnum.PRUVODCE_SRD_VH}`);
                        data.oznameni === ETypUdalosti.VYPIS &&
                            navigateWorkaround(`${location.pathname}${routePathEnum.PRUVODCE_SRD_VYPIS}`);
                        data.oznameni === ETypUdalosti.JINA && navigateWorkaround(`${routePathEnum.MAIN_PAGE}`);
                    }
                })
                .catch(() => {
                    setUploadCheckError(true);
                })
                .finally(() => (profily?.typprihlaseni !== 'PXKLIC' ? setLoading(false) : setLoadingPXklic(false)));

        data.zadani !== 'SRD_NOVA_ZPUSOB_XML' &&
            zalozeniSRDApi(value.ISIN)
                .then(resp => {
                    setLevel('idSRD', resp.data.id);
                    setLevel('isinSRD', data.isin);
                })
                .then(() => {
                    if (data.zadani === 'SRD_NOVA_ZPUSOB_XML')
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.KORPORATNI_AKCE}`);
                    else {
                        data.oznameni === ETypUdalosti.VH &&
                            navigateWorkaround(`${location.pathname}${routePathEnum.PRUVODCE_SRD_VH}`);
                        data.oznameni === ETypUdalosti.VYPIS &&
                            navigateWorkaround(`${location.pathname}${routePathEnum.PRUVODCE_SRD_VYPIS}`);
                        data.oznameni === ETypUdalosti.JINA && navigateWorkaround(`${routePathEnum.MAIN_PAGE}`);
                    }
                })
                .finally(() => setLoading(false));
    };

    return (
        <LoadingWrapper
            loading={loading || loadingPXklic}
            loadingText={profily?.typprihlaseni === 'PXKLIC' && loadingPXklic ? 'LOADING_PXKLIC' : 'NACITANI'}
        >
            <FormWrapper onFinish={onFinish} outForm={form}>
                <Title text="SRD_NOVA_NADPIS" level={4} />
                <Row>
                    <Col span={24}>
                        <SelectComponent
                            isFormItem={true}
                            name="SRD_NOVA_OZNAMENI"
                            labelParagraph="SRD_NOVA_OZNAMENI"
                            data={novaOznameni}
                            onChange={(val: string) =>
                                setData(pv => ({
                                    ...pv,
                                    oznameni: val as ETypUdalosti
                                }))
                            }
                            labelCol={{
                                span: 8
                            }}
                        />
                    </Col>
                </Row>
                {data.oznameni && (
                    <>
                        <Row>
                            <Col span={24}>
                                {menuProfile?.srd && menuProfile.srd !== 'CDCP' ? (
                                    <SelectComponent
                                        isFormItem={true}
                                        name="ISIN"
                                        labelParagraph="ISIN"
                                        data={dataIsin.map(({ isin }) => ({
                                            labelcz: isin,
                                            value: isin
                                        }))}
                                        onChange={(val: string) =>
                                            setData(pv => ({
                                                ...pv,
                                                isin: val
                                            }))
                                        }
                                        labelCol={{
                                            span: 8
                                        }}
                                    />
                                ) : (
                                    <InputComponent
                                        label="ISIN"
                                        capitalize={true}
                                        name="ISIN"
                                        type="text"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setData(pv => ({
                                                ...pv,
                                                isin: e.target.value
                                            }))
                                        }
                                        maxLength={12}
                                    />
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <SelectComponent
                                    isFormItem={true}
                                    name="SRD_NOVA_ZPUSOB"
                                    labelParagraph="SRD_NOVA_ZPUSOB"
                                    data={
                                        data.oznameni === ETypUdalosti.JINA
                                            ? zpusobZadani.filter(x => x.value === 'SRD_NOVA_ZPUSOB_XML')
                                            : zpusobZadani
                                    }
                                    onChange={(val: string) =>
                                        setData(pv => ({
                                            ...pv,
                                            zadani: val
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                />
                            </Col>
                        </Row>
                    </>
                )}
                {data.oznameni && data.isin && data.zadani === 'SRD_NOVA_ZPUSOB_XML' && (
                    <>
                        {data.oznameni !== ETypUdalosti.VYPIS && (
                            <DatePickerComponent
                                name="datumzverejneni"
                                label="DATUM_UVEREJNENI"
                                format={value => `${value.format('DD.MM.YYYY HH:mm')} (CET/CEST)`}
                                onChange={(val: dayjs.Dayjs | null) => {
                                    setData(pv => ({
                                        ...pv,
                                        ...(val && {
                                            datumzverejneni: val.utc().tz(tz).format()
                                        })
                                    }));
                                }}
                                showTime={true}
                                disabledDate={current => current < dayjs().tz(tz).subtract(1, 'days')}
                            />
                        )}

                        {data.oznameni === ETypUdalosti.VH && <Paragraph text="SRD_DETAIL_INFO_VH" />}
                        {data.oznameni === ETypUdalosti.VYPIS && <Paragraph text="SRD_DETAIL_INFO_VYPIS" />}
                        {data.oznameni === ETypUdalosti.JINA && <Paragraph text="SRD_DETAIL_INFO_JINA" />}

                        <Form.Item
                            style={{
                                marginTop: 16,
                                marginBottom: 40
                            }}
                            name="identifikaceosoby"
                            label={<Paragraph text={'SRD_NOVA_ZPUSOB_XML'} strong={true} />}
                            rules={[
                                {
                                    required: true,
                                    message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                }
                            ]}
                            validateTrigger="onSubmit"
                            extra={
                                uploadCheckError && (
                                    <Paragraph
                                        style={{
                                            display: 'block'
                                        }}
                                        text={'NEVALIDNI_XSD_SOUBOR'}
                                        className="extramessage-text"
                                    />
                                )
                            }
                        >
                            <div
                                style={{
                                    marginBottom: 22
                                }}
                            >
                                <UploadComponent
                                    buttonId="xmlUploadBtn"
                                    accept=".xml"
                                    showDownloadIcon={true}
                                    buttonLoading={uploading}
                                    fileList={fileList}
                                    onRemove={() => setFileList([])}
                                    buttonDisabled={fileList.length > 0}
                                    onChange={uplData => {
                                        if (uplData.event) setUploading(true);
                                        if (uplData.fileList)
                                            setFileList(
                                                uplData.fileList
                                                    .filter(f => f.status && f.status !== 'error')
                                                    .map(e => {
                                                        return {
                                                            // oid: resp.id!,
                                                            uid: e.uid!,
                                                            name: e.name.normalize()!,
                                                            fileName: e.fileName
                                                                ? e.fileName
                                                                : e.response
                                                                  ? e.response.tempnazev
                                                                  : ''!,
                                                            type: e.type!,
                                                            size: e.size!,
                                                            status: e.status ? e.status : 'error'
                                                        };
                                                    })
                                            );
                                        if (uplData.file.response) {
                                            setUploadCheckError(false);

                                            uplData.fileList
                                                .filter(f => f.status && f.status !== 'error')
                                                .map(e =>
                                                    setFileList([
                                                        {
                                                            uid: e.uid,
                                                            name: e.name.normalize(),
                                                            fileName: e.fileName
                                                                ? e.fileName
                                                                : e.response
                                                                  ? e.response.tempnazev
                                                                  : '',
                                                            type: e.type!,
                                                            size: e.size!,
                                                            status: e.status ? e.status : 'error'
                                                        }
                                                    ])
                                                );

                                            currentIsin !== data.isin
                                                ? zalozeniSRDApi(data.isin ? data.isin : '')
                                                      .then(resp => {
                                                          setCurrentIsin(data.isin ? data.isin : '');
                                                          setLevel('idSRD', resp.data.id);
                                                          setLevel('isinSRD', data.isin);
                                                      })
                                                      .finally(() => setUploading(false))
                                                : setUploading(false);
                                        }
                                    }}
                                />
                            </div>
                        </Form.Item>
                        <Modal
                            title={<TranslateText>{'SOUBOR'}</TranslateText>}
                            // open={openModal}
                            onCancel={() => onFinish(form.getFieldsValue())}
                            afterClose={() => onFinish(form.getFieldsValue())}
                            footer={null}
                        >
                            <Row>
                                <Paragraph text="SOUBOR_200" />
                            </Row>
                            <Row className="button-row">
                                <ButtonComponent
                                    id="pokracovatModalBtn"
                                    buttontext="POKRACOVAT"
                                    onClick={() => onFinish(form.getFieldsValue())}
                                />
                            </Row>
                        </Modal>
                    </>
                )}
                {data.zadani && (
                    <ButtonComponent
                        id="submitBtn"
                        onClick={() => form.submit()}
                        buttontext={data.zadani !== 'SRD_NOVA_ZPUSOB_XML' ? 'POKRACOVAT' : 'ODESLAT'}
                        loading={loading || uploading}
                    />
                )}
            </FormWrapper>
        </LoadingWrapper>
    );
};
